/**
 * Settings: the business, the shapes it rosters, and the rules the app enforces.
 *
 * Grouped by who the setting affects rather than by which table it lives in.
 * The super rate and the notice window carry their consequences in the hint
 * text, because both are numbers whose effect is invisible until a pay run or a
 * refused unavailability request makes it visible weeks later.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, Building2, Clock, Palette, Plus, Trash2 } from 'lucide-react';
import {
  api,
  ApiError,
  qk,
  type Area,
  type DingRoutineEntry,
  type SettingsResponse,
  type ShiftTemplate,
} from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { fmtDuration, fmtTimeRange, shiftMinutes } from '../../lib/time.ts';
import { COLOUR_NAMES, colourTokens, ErrorState, Field, LoadingBlock, Spinner } from '../../components/Bits.tsx';
import { OutcomeModal } from '../../components/Overlay.tsx';

const DING_EVENTS: Array<{ value: string; label: string; help: string }> = [
  { value: 'roster_published', label: 'The roster is published', help: 'Everybody rostered that week is told.' },
  { value: 'shift_reminder', label: 'Before a shift starts', help: 'A nudge ahead of each shift.' },
  { value: 'shift_changed', label: 'A shift of theirs changes', help: 'Times moved, area changed or cancelled.' },
  { value: 'payslip_ready', label: 'A payslip is ready', help: 'Sent when a pay run is finalised.' },
];

function Section({
  icon,
  title,
  description,
  children,
}: {
  icon: ReactNode;
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <section className="card mb-5 p-5">
      <header className="mb-4 flex items-start gap-3">
        <span
          className="flex h-9 w-9 shrink-0 items-center justify-center"
          style={{
            borderRadius: 'var(--sp-radius-sm)',
            background: 'var(--sp-accent-soft)',
            color: 'var(--sp-accent-strong)',
            border: '1px solid var(--sp-accent-border)',
          }}
          aria-hidden="true"
        >
          {icon}
        </span>
        <div className="min-w-0">
          <h2 className="text-[15px] font-bold text-ink">{title}</h2>
          <p className="text-[12.5px] leading-relaxed text-muted">{description}</p>
        </div>
      </header>
      {children}
    </section>
  );
}

function ColourPicker({ value, onChange }: { value: string | null; onChange: (v: string) => void }) {
  return (
    <span className="flex items-center gap-1.5">
      {COLOUR_NAMES.map((c) => {
        const tokens = colourTokens(c);
        const selected = (value ?? 'slate') === c;
        return (
          <button
            key={c}
            type="button"
            onClick={() => onChange(c)}
            aria-pressed={selected}
            aria-label={`${c} colour`}
            title={`${c[0]?.toUpperCase()}${c.slice(1)}|The colour this shows as on the roster grid.`}
            style={{
              width: 22,
              height: 22,
              borderRadius: 'var(--sp-radius-pill)',
              background: tokens.solid,
              border: selected ? '2px solid var(--sp-text)' : '2px solid transparent',
              boxShadow: 'var(--sp-shadow-1)',
            }}
          />
        );
      })}
    </span>
  );
}

export default function SettingsPage() {
  const { tenant, isOwner } = useSession();
  const slug = tenant?.tenantSlug ?? '';
  const qc = useQueryClient();
  const [outcome, setOutcome] = useState<{ ok: boolean; title: string; message: string } | null>(null);

  const query = useQuery({ queryKey: qk.settings(slug), queryFn: () => api.settings(slug), enabled: slug !== '' });
  const [draft, setDraft] = useState<SettingsResponse | null>(null);

  useEffect(() => {
    if (query.data) setDraft(query.data);
  }, [query.data]);

  const invalidate = () => void qc.invalidateQueries({ queryKey: qk.settings(slug) });
  const fail = (err: unknown, what: string) =>
    setOutcome({
      ok: false,
      title: `Could not ${what}`,
      message: err instanceof ApiError ? err.message : `Something went wrong and we could not ${what}.`,
    });

  const saveSettings = useMutation({
    mutationFn: () =>
      api.updateSettings(slug, {
        name: draft?.tenant.name,
        abn: draft?.tenant.abn,
        address: draft?.tenant.address,
        phone: draft?.tenant.phone,
        timezone: draft?.tenant.timezone,
        settings: draft?.settings,
      }),
    onSuccess: () => {
      invalidate();
      setOutcome({ ok: true, title: 'Settings saved', message: 'Everything on this page is up to date.' });
    },
    onError: (e) => fail(e, 'save those settings'),
  });

  const addTemplate = useMutation({
    mutationFn: () =>
      api.createTemplate(slug, { name: 'New shift', startTime: '09:00', endTime: '17:00', colour: 'green' }),
    onSuccess: invalidate,
    onError: (e) => fail(e, 'add that shift'),
  });
  const patchTemplate = useMutation({
    mutationFn: (v: { id: string; body: Partial<ShiftTemplate> }) => api.updateTemplate(slug, v.id, v.body),
    onSuccess: invalidate,
    onError: (e) => fail(e, 'save that shift'),
  });
  const removeTemplate = useMutation({
    mutationFn: (id: string) => api.deleteTemplate(slug, id),
    onSuccess: invalidate,
    onError: (e) => fail(e, 'delete that shift'),
  });

  const addArea = useMutation({
    mutationFn: () => api.createArea(slug, { name: 'New area', colour: 'blue' }),
    onSuccess: invalidate,
    onError: (e) => fail(e, 'add that area'),
  });
  const patchArea = useMutation({
    mutationFn: (v: { id: string; body: Partial<Area> }) => api.updateArea(slug, v.id, v.body),
    onSuccess: invalidate,
    onError: (e) => fail(e, 'save that area'),
  });
  const removeArea = useMutation({
    mutationFn: (id: string) => api.deleteArea(slug, id),
    onSuccess: invalidate,
    onError: (e) => fail(e, 'delete that area'),
  });

  if (query.isLoading || !draft) {
    if (query.isError) return <ErrorState error={query.error} what="settings" onRetry={() => void query.refetch()} />;
    return <LoadingBlock label="Loading settings" />;
  }

  const setTenantField = (k: keyof SettingsResponse['tenant'], v: string) =>
    setDraft((d) => (d ? { ...d, tenant: { ...d.tenant, [k]: v } } : d));
  const setSetting = <K extends keyof SettingsResponse['settings']>(k: K, v: SettingsResponse['settings'][K]) =>
    setDraft((d) => (d ? { ...d, settings: { ...d.settings, [k]: v } } : d));

  const routine: DingRoutineEntry[] = draft.settings.dingRoutine ?? [];

  return (
    <div className="mx-auto max-w-3xl px-4 py-6">
      <header className="mb-5 flex flex-wrap items-end gap-3">
        <div className="min-w-0 flex-1">
          <h1 className="text-[20px] font-extrabold tracking-tight text-ink">Settings</h1>
          <p className="text-[13px] text-muted">{draft.tenant.name}</p>
        </div>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => saveSettings.mutate()}
          disabled={saveSettings.isPending}
        >
          {saveSettings.isPending ? <Spinner size={15} label="Saving" /> : null}
          Save changes
        </button>
      </header>

      <Section
        icon={<Building2 size={17} />}
        title="Business details"
        description="These appear on every payslip, so they need to match your registration."
      >
        <Field label="Trading name" id="set-name">
          <input id="set-name" className="field" value={draft.tenant.name} onChange={(e) => setTenantField('name', e.target.value)} />
        </Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="ABN" id="set-abn" hint="Required on a compliant payslip.">
            <input
              id="set-abn"
              className="field tabular"
              value={draft.tenant.abn ?? ''}
              onChange={(e) => setTenantField('abn', e.target.value)}
              placeholder="Add the ABN"
            />
          </Field>
          <Field label="Phone" id="set-phone">
            <input
              id="set-phone"
              className="field"
              type="tel"
              value={draft.tenant.phone ?? ''}
              onChange={(e) => setTenantField('phone', e.target.value)}
              placeholder="Add a phone number"
            />
          </Field>
        </div>
        <Field label="Address" id="set-address">
          <input
            id="set-address"
            className="field"
            value={draft.tenant.address ?? ''}
            onChange={(e) => setTenantField('address', e.target.value)}
          />
        </Field>
      </Section>

      <Section
        icon={<Palette size={17} />}
        title="Your usual shifts"
        description="The shapes you roster most often. Drag them straight onto the grid."
      >
        {draft.templates.length === 0 ? (
          <p className="mb-3 text-[13px] text-muted">None set up yet. Add the shifts you run every week, like the open, the lunch and the close, then drop them straight onto the roster.</p>
        ) : null}
        {draft.templates.map((t) => (
          <div key={t.id} className="mb-3">
          <div className="flex flex-wrap items-center gap-2">
            <input
              className="field"
              style={{ width: 150 }}
              defaultValue={t.name}
              aria-label={`Name of the ${t.name} template`}
              onBlur={(e) => e.target.value !== t.name && patchTemplate.mutate({ id: t.id, body: { name: e.target.value } })}
            />
            <input
              className="field tabular"
              type="time"
              step={300}
              style={{ width: 118 }}
              defaultValue={t.startTime.slice(0, 5)}
              aria-label={`Start time of the ${t.name} template`}
              onBlur={(e) => patchTemplate.mutate({ id: t.id, body: { startTime: e.target.value } })}
            />
            <input
              className="field tabular"
              type="time"
              step={300}
              style={{ width: 118 }}
              defaultValue={t.endTime.slice(0, 5)}
              aria-label={`Finish time of the ${t.name} template`}
              onBlur={(e) => patchTemplate.mutate({ id: t.id, body: { endTime: e.target.value } })}
            />
            <ColourPicker value={t.colour} onChange={(c) => patchTemplate.mutate({ id: t.id, body: { colour: c } })} />
            <span className="flex-1" />
            <button
              type="button"
              className="btn btn-icon btn-sm"
              onClick={() => removeTemplate.mutate(t.id)}
              title={`Delete the ${t.name} template|Shifts already on the roster are not affected.`}
              aria-label={`Delete the ${t.name} template`}
            >
              <Trash2 size={14} aria-hidden="true" />
            </button>
          </div>
          {/* The 12 hour reading of the two time boxes, on its own line. Beside
              them it competed for width and pushed the delete button onto a
              line of its own. */}
          <span className="tabular mt-1 block pl-1 text-[11.5px] text-faint">
            {fmtTimeRange(t.startTime.slice(0, 5), t.endTime.slice(0, 5))}, {fmtDuration(shiftMinutes(t.startTime.slice(0, 5), t.endTime.slice(0, 5)))}
          </span>
          </div>
        ))}
        <button type="button" className="btn btn-sm mt-2" onClick={() => addTemplate.mutate()}>
          <Plus size={14} aria-hidden="true" />
          Add a template
        </button>
      </Section>

      <Section
        icon={<Palette size={17} />}
        title="Areas"
        description="Sections of the floor. Shifts can be grouped by area on the roster."
      >
        {draft.areas.map((a) => (
          <div key={a.id} className="mb-2 flex flex-wrap items-center gap-2">
            <input
              className="field"
              style={{ width: 200 }}
              defaultValue={a.name}
              aria-label={`Name of the ${a.name} area`}
              onBlur={(e) => e.target.value !== a.name && patchArea.mutate({ id: a.id, body: { name: e.target.value } })}
            />
            <ColourPicker value={a.colour} onChange={(c) => patchArea.mutate({ id: a.id, body: { colour: c } })} />
            <span className="flex-1" />
            <button
              type="button"
              className="btn btn-icon btn-sm"
              onClick={() => removeArea.mutate(a.id)}
              title={`Delete the ${a.name} area|Shifts in it become shifts with no area.`}
              aria-label={`Delete the ${a.name} area`}
            >
              <Trash2 size={14} aria-hidden="true" />
            </button>
          </div>
        ))}
        <button type="button" className="btn btn-sm mt-2" onClick={() => addArea.mutate()}>
          <Plus size={14} aria-hidden="true" />
          Add an area
        </button>
      </Section>

      <Section
        icon={<Clock size={17} />}
        title="Rules"
        description="How far ahead the team has to tell you, and what you put aside for super."
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <Field
            label="Unavailability notice"
            id="set-notice"
            hint={`Days ahead. Your team cannot mark a day unavailable inside ${
              draft.settings.unavailabilityNoticeDays ?? 0
            } days of it, and they are told why.`}
          >
            <input
              id="set-notice"
              className="field tabular"
              type="number"
              min={0}
              max={60}
              value={draft.settings.unavailabilityNoticeDays ?? 0}
              onChange={(e) => setSetting('unavailabilityNoticeDays', Number(e.target.value) || 0)}
            />
          </Field>
          <Field
            label="Super rate"
            id="set-super"
            hint="Per cent of gross. The statutory rate has been 12 per cent since 1 July 2025."
          >
            <input
              id="set-super"
              className="field tabular"
              type="number"
              min={0}
              max={30}
              step="0.5"
              disabled={!isOwner}
              value={draft.settings.superRatePercent ?? 12}
              onChange={(e) => setSetting('superRatePercent', Number(e.target.value) || 0)}
            />
          </Field>
        </div>
        {(draft.settings.superRatePercent ?? 12) < 12 ? (
          <div
            className="rounded-card border p-3.5 text-[13px] leading-relaxed"
            style={{ background: 'var(--sp-warn-soft)', borderColor: 'var(--sp-warn-border)' }}
          >
            <strong className="block text-ink">That is below the statutory rate.</strong>
            <span className="text-muted">
              The superannuation guarantee has been 12 per cent since 1 July 2025. Paying less than the statutory
              rate is a shortfall you would owe later, with charges on top.
            </span>
          </div>
        ) : null}
      </Section>

      <Section
        icon={<Bell size={17} />}
        title="Ding routine"
        description="When the app notifies your team. Everybody gets these; they can add more for themselves, never fewer."
      >
        {routine.length === 0 ? (
          <p className="mb-3 text-[13px] text-muted">No dings set. Your team will not be told when a roster goes out.</p>
        ) : null}
        {routine.map((d, i) => {
          const meta = DING_EVENTS.find((e) => e.value === d.event);
          return (
            <div key={`${d.event}-${i}`} className="mb-2 flex flex-wrap items-center gap-2">
              <select
                className="field"
                style={{ width: 220 }}
                aria-label="Ding event"
                value={d.event}
                onChange={(e) =>
                  setSetting(
                    'dingRoutine',
                    routine.map((r, j) => (j === i ? { ...r, event: e.target.value } : r)),
                  )
                }
              >
                {DING_EVENTS.map((e) => (
                  <option key={e.value} value={e.value}>
                    {e.label}
                  </option>
                ))}
              </select>
              <input
                className="field tabular"
                type="number"
                step={1}
                style={{ width: 96 }}
                aria-label="Hours before or after the event"
                value={d.offsetHours}
                onChange={(e) =>
                  setSetting(
                    'dingRoutine',
                    routine.map((r, j) => (j === i ? { ...r, offsetHours: Number(e.target.value) || 0 } : r)),
                  )
                }
              />
              <span className="text-[12.5px] text-muted">
                {d.offsetHours === 0
                  ? 'straight away'
                  : d.offsetHours < 0
                    ? `${Math.abs(d.offsetHours)} hours before`
                    : `${d.offsetHours} hours after`}
                {meta ? `. ${meta.help}` : ''}
              </span>
              <span className="flex-1" />
              <button
                type="button"
                className="btn btn-icon btn-sm"
                onClick={() => setSetting('dingRoutine', routine.filter((_, j) => j !== i))}
                title="Remove this ding|Your team stops being notified for this event."
                aria-label="Remove this ding"
              >
                <Trash2 size={14} aria-hidden="true" />
              </button>
            </div>
          );
        })}
        <button
          type="button"
          className="btn btn-sm mt-2"
          onClick={() => setSetting('dingRoutine', [...routine, { event: 'shift_reminder', offsetHours: -12 }])}
        >
          <Plus size={14} aria-hidden="true" />
          Add a ding
        </button>
        <p className="mt-3 text-[12.5px] leading-relaxed text-faint">
          Changes here take effect on save, and apply to everyone.
        </p>
      </Section>

      <OutcomeModal
        open={outcome !== null}
        onClose={() => setOutcome(null)}
        ok={outcome?.ok ?? true}
        title={outcome?.title ?? ''}
        message={outcome?.message ?? ''}
      />
    </div>
  );
}
